import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import toColorString from 'polished/lib/color/toColorString';

import color from '@decisiv/design-tokens/lib/color';

import InfoCircleF from '@decisiv/iconix/lib/components/InfoCircleF';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { H1, P } from '@decisiv/ui-components/lib/components/Typography';

import OverflowPopover from '../../shared/OverflowPopover';
import roleTag, { PERMISSION_ASSIGNMENT } from '../../shared/roleTag';
// The users list's summary tag for an account holding more than one role.
// Imported rather than restated so the two screens cannot show the same state
// under two different words.
import { MIXED_PERMISSIONS } from '../../Users/components/PermissionsCell';
import { PanelRow } from '../../shared/styles';
import EMPTY_VALUE from '../../shared/emptyValue';
import {
  AssignmentRow,
  HeaderBar,
  HeaderIdentity,
  LastLogin,
  TitlePrefix,
} from './styles';

// How many accounts sit inline before the rest collapse behind the counter,
// matching the design (Figma 787:77246): two accounts, then "1 ⌄".
export const INLINE_ASSIGNMENTS = 2;

/**
 * Pairs regrouped so one account is one entry, in first-seen order. The prop
 * stays the flat {account, permission} list UserAssignments#pairs returns, but
 * the header shows one tag per account (Figma 787:77630).
 *
 * Grouped on `accountId` where the caller supplies one, falling back to the
 * name. Dealer names are not unique, and two accounts sharing one would
 * otherwise merge into an entry reporting the union of their roles as "Mixed
 * Permissions" — roles neither actually holds together.
 */
const groupKeyOf = ({ account, accountId }) => accountId || account;

const groupByAccount = (assignments) =>
  assignments.reduce((accounts, assignment) => {
    const { account, permission } = assignment;
    const groupKey = groupKeyOf(assignment);
    const existing = accounts.find((entry) => entry.groupKey === groupKey);

    if (!existing) {
      return [...accounts, { groupKey, account, permissions: [permission] }];
    }

    if (!existing.permissions.includes(permission)) {
      existing.permissions.push(permission);
    }

    return accounts;
  }, []);

/**
 * One account and the roles held there. `collapsed` is the header bar's shape:
 * several roles become one "Mixed Permissions" tag with a popover behind it.
 *
 * Not available inside the overflow panel, which is itself a popover: a nested
 * trigger inherits `pointer-events: none` and the roles cannot be reached at
 * all. So the panel spells them out, which is what its rows are for anyway.
 */
const assignmentPair = ({ account, permissions }, { collapsed = true } = {}) => (
  <>
    <Tag text={account} variant="outline" />
    {permissions.length === 1 && roleTag(permissions[0])}
    {permissions.length > 1 &&
      (collapsed ? (
        <OverflowPopover
          ariaLabel={`Show all roles for ${account}`}
          summary={
            <Tag color="information" palette="pastel" text={MIXED_PERMISSIONS} />
          }
        >
          {permissions.map((permission) => (
            <PanelRow key={permission}>{roleTag(permission)}</PanelRow>
          ))}
        </OverflowPopover>
      ) : (
        permissions.map((permission) => (
          <Fragment key={permission}>{roleTag(permission)}</Fragment>
        ))
      ))}
  </>
);

/**
 * Everything above the tabs: "Edit {name}", the account/role pairs the user holds,
 * and the ecosystem Last Login at top right.
 *
 * The breadcrumb above it is mounted by EditUser, not here — see the note there.
 */
function UserHeader({ fullName, lastLoginAt, permissionAssignments }) {
  const byAccount = groupByAccount(permissionAssignments);
  const inline = byAccount.slice(0, INLINE_ASSIGNMENTS);
  const rest = byAccount.slice(INLINE_ASSIGNMENTS);

  return (
    <HeaderBar>
      <HeaderIdentity>
        <H1 style={{ margin: 0 }}>
          <TitlePrefix>Edit </TitlePrefix>
          {fullName}
        </H1>

        {byAccount.length > 0 && (
          <AssignmentRow>
            {/* Index keys throughout: both halves render the same derived list,
                  which is never reordered, and a key composed from the account and
                  role can collide when either contains a hyphen — "A-B"/"C" against
                  "A"/"B-C". Keying the two branches differently would mean one of
                  them was wrong. */}
            {inline.map((assignment, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <PanelRow key={index}>{assignmentPair(assignment)}</PanelRow>
            ))}

            {rest.length > 0 && (
              <OverflowPopover
                ariaLabel={`Show all accounts and roles (${rest.length} more)`}
                count={rest.length}
              >
                {rest.map((assignment, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <PanelRow key={index}>
                    {assignmentPair(assignment, { collapsed: false })}
                  </PanelRow>
                ))}
              </OverflowPopover>
            )}
          </AssignmentRow>
        )}
      </HeaderIdentity>

      <LastLogin>
        <InfoCircleF color={toColorString(color.status.information.medium)} />
        <P shade={1} size="small">
          Last Login {lastLoginAt || EMPTY_VALUE}
        </P>
      </LastLogin>
    </HeaderBar>
  );
}

UserHeader.propTypes = {
  fullName: PropTypes.string,
  /** Preformatted by the server; absent when the user has never logged in. */
  lastLoginAt: PropTypes.string,
  permissionAssignments: PropTypes.arrayOf(PERMISSION_ASSIGNMENT),
};

UserHeader.defaultProps = {
  fullName: undefined,
  lastLoginAt: undefined,
  permissionAssignments: [],
};

export default UserHeader;
